import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Search, X } from "lucide-react";
import Reveal from "../components/Reveal";
import { BLOG_CATEGORIES, filterPosts } from "../blog";

export default function Blog() {
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState(null);

  const posts = useMemo(
    () => filterPosts({ category: active, query, tag }),
    [active, query, tag]
  );

  const clearFilters = () => {
    setQuery("");
    setTag(null);
    setActive("all");
  };

  const hasFilters = query || tag || active !== "all";

  return (
    <div>
      {/* Header */}
      <section className="bg-gradient-to-b from-[#effaf9] to-white pt-16 pb-8 md:pt-20 md:pb-10">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-[var(--teal-dark)]">
              Holistic Intuitive Nutrition
            </p>
            <h1 className="font-serif-brand text-5xl md:text-6xl text-purple mt-3">Wellness Insights</h1>
            <div className="brand-rule mx-auto mt-5" />
            <p className="text-neutral-600 mt-6 max-w-2xl mx-auto leading-relaxed">
              Insights on holistic health &amp; wellness to support your journey toward a
              healthier, more balanced life.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Search */}
      <section className="max-w-2xl mx-auto px-5 pt-2">
        <div className="relative">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles, topics, or tags..."
            className="w-full pl-11 pr-11 py-3.5 rounded-full border border-neutral-200 bg-white text-neutral-700 text-sm outline-none focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/20 transition-all"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </section>

      {/* Category filter */}
      <section className="max-w-6xl mx-auto px-5 pt-6">
        <div className="flex flex-wrap justify-center gap-2 md:gap-3">
          {BLOG_CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={`px-5 py-2.5 rounded-full text-xs font-semibold tracking-wide-sm uppercase transition-all duration-200 border ${
                active === c.id
                  ? "bg-[var(--teal)] text-white border-[var(--teal)]"
                  : "bg-white text-neutral-600 border-neutral-200 hover:border-[var(--teal)] hover:text-[var(--teal-dark)]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Results meta */}
      <section className="max-w-6xl mx-auto px-5 pt-8">
        <div className="flex items-center justify-center gap-3 text-sm text-neutral-500">
          <span>
            {posts.length} {posts.length === 1 ? "article" : "articles"}
          </span>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-[var(--teal-dark)] font-semibold hover:underline"
            >
              <X size={13} /> Clear filters
            </button>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-5 py-10 md:py-14">
        {posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-serif-brand text-3xl text-purple">No articles found</p>
            <p className="text-neutral-500 mt-3">
              Try a different search term or clear your filters.
            </p>
            <button onClick={clearFilters} className="btn-brand btn-outline-teal mt-6">
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post, i) => (
              <Reveal key={post.slug} delay={(i % 3) * 100}>
                <Link
                  to={`/blog/${post.slug}`}
                  className="group block h-full bg-white rounded-xl overflow-hidden border border-neutral-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 flex flex-col">
                    <div className="flex items-center gap-3 text-[11px] text-neutral-400 uppercase tracking-wide-sm mb-3">
                      <span>{post.date}</span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} /> {post.readTime}
                      </span>
                    </div>
                    <h2 className="font-serif-brand text-2xl text-purple leading-snug mb-3 group-hover:text-[var(--purple-dark)] transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-neutral-600 text-[0.9rem] leading-relaxed line-clamp-3">
                      {post.excerpt}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-2 text-[var(--teal-dark)] font-semibold tracking-wide-sm uppercase text-xs group-hover:gap-3 transition-all">
                      Read Article <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
